import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { useAuth } from 'auth-sso-kit/client/index.js';
import { AlertTriangle, ArrowLeftRight, CheckCircle2, Clock, Copy, ExternalLink, UserCheck } from 'lucide-react';
import { api } from '../api/client';
import { naviguer } from '../App';
import { useRole } from '../context/RoleContext';
import { useMeta } from '../components/demande/useMeta';
import { PersonneSelect } from '../components/demande/PersonneSelect';
import { surchargeCarte, type ContexteMessageSlack } from '../components/demande/parcours';
import { RetourCarte } from '../components/demande/RetourCarte';
import { BoutonCopier } from '../components/messages/BoutonCopier';
import { MessageSlack } from '../components/messages/MessageSlack';
import { MessagesEmails } from '../components/messages/MessagesEmails';
import { MessagesSms } from '../components/messages/MessagesSms';
import { quand as quandLisible } from '../components/messages/format';
import { annoncerMiseAJourMessages } from '../components/messages/useCompteMessages';
import { PoleDot } from '../components/demande/PoleDot';
import { FicheFakOverlay } from '../components/fak/FicheFakOverlay';
import { aFra } from '../components/demande/DemandesCollegues';
import { ProposerRemplacementModal } from '../components/demande/ProposerRemplacementModal';
import { BadgeDatesFlexibles } from '../components/demande/BadgeDatesFlexibles';
import { PenseAVous } from '../components/demande/PenseAVous';
import { PrevenusPersonnellement } from '../components/demande/PrevenusPersonnellement';
import { useMaPersonne } from '../components/demande/useMaPersonne';
import { horaireCourt, jourLocalIso } from '../components/demande/horaire';
import { OrigineBadge, StatutBadge, TypeBadge } from '../components/demande/StatutBadge';
import {
  ACTIONS_DANGER, ACTIONS_NEUTRES, BANNIERE_TIPEE, lienTipee, classeBadgeFak, classeEcheance, DESCRIPTIONS_ACTION, formatDateCourt,
  formatDateHeure, formatDateLongue, formatRelatif, LIBELLES_ACTION, LIBELLES_CANAL, LIBELLES_STATUT_FAK,
  libelleEvenement, STATUT_META,
} from '../components/demande/format';
import {
  type NiveauUrgence, type SeuilsUrgence, SEUILS_URGENCE_PAR_DEFAUT, joursAvant, niveauUrgence, seuilsDepuisSettings,
} from '../components/demande/urgence';
import {
  type ActeurParcours, PHASES_PARCOURS, phaseCourante, infoBandeau,
} from '../components/demande/parcours';
import type { Action, Affectation, Annulation, Atelier, Demande, DemandeEvent, FakFeedback, FakKit, Fusion, ListePresence, MessageDemande, ParticipantProgramme, Passation, Recherche, RetourDemande } from '../types';

// Fiche demande — le cœur de l'appli : en-tête + panneau d'actions (toutes
// les transitions légales pour l'utilisateur·rice courant·e, fournies par le
// serveur via demande.actions_possibles) + onglets Recherche/Passation/
// Présences/FRA/Historique. Chaque action passe par api.appliquerAction ; le
// serveur reste la seule source de vérité de la machine à états.
const STATUTS_CONCERNANT_FUSION = ['fusion_demandee', 'evaluation_annulation', 'fusionnee'];
const STATUTS_CONCERNANT_ANNULATION = ['evaluation_annulation', 'annulee'];

type Onglet = 'recherche' | 'passation' | 'presences' | 'fak' | 'historique';
const ONGLETS: { cle: Onglet; libelle: string }[] = [
  { cle: 'recherche', libelle: 'Recherche' },
  { cle: 'passation', libelle: 'Passation' },
  { cle: 'presences', libelle: 'Présences' },
  { cle: 'fak', libelle: 'FRA' },
  { cle: 'historique', libelle: 'Historique' },
];

type ModalState =
  | null
  | { type: 'remplacant' }
  | { type: 'motif'; action: Action; label: string }
  | { type: 'fusion'; action: Action }
  | { type: 'cloture' }
  | { type: 'confirmer_refus' }
  | { type: 'tipee_completer' }
  | { type: 'confirmer'; action: Action };

interface ReponseForm { personneId: number | null; positive: boolean; commentaire: string; }

// Initiales pour l'avatar de l'en-tête : première lettre du premier et du
// dernier mot du nom affiché (un seul mot → ses deux premières lettres).
function initialesDe(nom: string | null | undefined): string {
  const parts = (nom ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Jour/jour-de-semaine/mois séparés pour la pastille de date (mêmes bornes
// UTC-midnight-local que format.ts pour éviter un décalage d'un jour).
function partiesDate(iso: string): { dow: string; jour: number; mois: string } {
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  return {
    dow: new Intl.DateTimeFormat('fr-CH', { weekday: 'short' }).format(d).replace('.', ''),
    jour: d.getDate(),
    mois: new Intl.DateTimeFormat('fr-CH', { month: 'short' }).format(d).replace('.', ''),
  };
}

// Couleur de la pastille de date selon l'urgence — uniquement pour une
// demande encore ouverte (une demande terminée ou une séance passée n'a plus
// d'urgence qui compte → gris neutre). Reprend les niveaux configurables.
const COULEUR_NIVEAU: Record<NiveauUrgence, string> = {
  urgent: 'var(--err)',
  presse: 'var(--warn)',
  alancer: 'var(--accent)',
  anticipe: 'var(--ink-3)',
  depasse: 'var(--ink-3)',
};

// Bandeau « où en est-on » : couleur + icône par acteur qui tient la balle.
const COULEUR_ACTEUR: Record<ActeurParcours, string> = {
  gestion: 'var(--accent)',
  remplacant: 'var(--parcours-remp)',
  absent: 'var(--ok)',
  termine: 'var(--ok)',
  alerte: 'var(--err)',
};
const ICONE_ACTEUR: Record<ActeurParcours, typeof Clock> = {
  gestion: Clock,
  remplacant: UserCheck,
  absent: ArrowLeftRight,
  termine: CheckCircle2,
  alerte: AlertTriangle,
};

function alerteErreur(message: string) {
  return (
    <div
      className="badge badge-err mb-16"
      role="alert"
      style={{ display: 'block', padding: '8px 12px', textTransform: 'none', letterSpacing: 'normal', fontSize: 'var(--t-13)' }}
    >
      {message}
    </div>
  );
}

export function DemandePage({ id, params }: { id: number; params?: Record<string, string> }) {
  const { meta } = useMeta();
  const { user } = useAuth();
  const { peutGerer, aRole } = useRole();
  const estRh = aRole('rh');

  const [demande, setDemande] = useState<Demande | null>(null);
  const [events, setEvents] = useState<DemandeEvent[]>([]);
  const [affectation, setAffectation] = useState<Affectation | null>(null);
  const [passation, setPassation] = useState<Passation | null>(null);
  const [presences, setPresences] = useState<ListePresence | null>(null);
  const [retourInfo, setRetourInfo] = useState<RetourDemande | null>(null);
  const [recherches, setRecherches] = useState<Recherche[]>([]);
  // Messages Slack / e-mails / SMS de la demande avec leur état (le serveur ne renvoie que ce que cette personne a le droit de voir).
  const [messages, setMessages] = useState<MessageDemande[]>([]);
  const [marquageEnCours, setMarquageEnCours] = useState(false);
  const carteEtape = useRef<HTMLDivElement>(null);          // la carte « Prochaine étape » : le focus y revient quand son bouton disparaît
  const [annonceMessage, setAnnonceMessage] = useState('');
  const [fusion, setFusion] = useState<Fusion | null>(null);
  const [annulation, setAnnulation] = useState<Annulation | null>(null);
  const [participants, setParticipants] = useState<ParticipantProgramme[]>([]);
  const [fak, setFak] = useState<FakKit | null>(null);
  const [fakFeedback, setFakFeedback] = useState<FakFeedback[]>([]);

  const [loading, setLoading] = useState(true);
  const [erreurChargement, setErreurChargement] = useState<string | null>(null);
  // Fiche en lecture seule d'un·e collègue : « Je me propose » (fenêtre de récapitulatif) et FRA en aperçu.
  const maPersonne = useMaPersonne([]);
  const [proposerOuvert, setProposerOuvert] = useState(false);
  const [ficheFraOuverte, setFicheFraOuverte] = useState(false);
  // Après « Je me propose » la fiche se recharge en accès complet (on est alors le/la remplaçant·e désigné·e) :
  // ce message dit ce qui vient de se passer.
  const [propositionFaite, setPropositionFaite] = useState<'ok' | 'partiel' | null>(null);
  // Onglet initial depuis le lien profond (#demandes/12?onglet=fak — utilisé
  // par l'invitation « laisser un retour FRA » du lendemain d'atelier).
  const [onglet, setOnglet] = useState<Onglet>(() => {
    const o = params?.onglet;
    return o === 'recherche' || o === 'passation' || o === 'presences' || o === 'fak' || o === 'historique' ? o : 'recherche';
  });

  // Seuils d'urgence (jours avant la séance) — mêmes réglages que le Tableau
  // de bord/Calendrier, pour la couleur de la pastille de date de l'en-tête.
  const [seuils, setSeuils] = useState<SeuilsUrgence>(SEUILS_URGENCE_PAR_DEFAUT);
  useEffect(() => {
    api.getSettings().then((s) => setSeuils(seuilsDepuisSettings(s))).catch(() => {});
  }, []);

  // ── Chargement (initial + après chaque action) ─────────────────────────
  // La fiche affichée peut changer pendant qu'un chargement dure (on ouvre une autre demande) : une réponse
  // de l'ancienne fiche ne doit jamais s'écrire par-dessus la nouvelle, alors que les actions partent sur l'id courant.
  const idAffiche = useRef(id);
  idAffiche.current = id;
  async function charger() {
    const idCharge = id;
    const perime = () => idAffiche.current !== idCharge;
    setErreurChargement(null);
    const d = await api.getDemande(idCharge);
    if (perime()) return;
    setDemande(d);
    if (d.acces === 'limite' || d.acces === 'close') {
      // Lecture seule : aucune sous-ressource (le serveur les refuse).
      setEvents([]); setAffectation(null); setPassation(null); setPresences(null); setRecherches([]); setRetourInfo(null); setMessages([]);
      setParticipants([]); setFak(null); setFakFeedback([]); setFusion(null); setAnnulation(null);
      return;
    }
    // Étape 7 « Retour » : seulement quand l'atelier est donné (ou clôturé, pour relire) ; refusé aux personnes non concernées.
    const aUnRetour = d.statut === 'atelier_donne' || d.statut === 'cloturee';
    const [ev, aff, pas, pres, rech, part, fakDetail, ret, msgs] = await Promise.all([
      api.getDemandeEvents(idCharge),
      api.getAffectation(idCharge),
      api.getPassation(idCharge),
      api.getPresences(idCharge),
      api.getRecherches(idCharge),
      // Réservé à la gestion, au/à la titulaire et au/à la remplaçant·e désigné·e :
      // pour les autres le serveur répond 403 — la fiche se charge quand même.
      api.getParticipants(d.atelier_id, d.date_atelier).catch(() => [] as ParticipantProgramme[]),
      api.getFak(d.atelier_id),
      aUnRetour ? api.getRetour(idCharge).catch(() => null) : Promise.resolve(null),
      // Les messages sont un confort de lecture : un échec ne bloque jamais la fiche.
      api.getMessagesDemande(idCharge).catch(() => [] as MessageDemande[]),
    ]);
    if (perime()) return;
    const fakKit = fakDetail.fak;
    // Les dernières lectures (retours FRA, fusion, annulation) attendent aussi : tout s'écrit d'un bloc, ou rien.
    const [retours, fus, annul] = await Promise.all([
      fakKit ? api.getFakFeedback(fakKit.id) : Promise.resolve([] as FakFeedback[]),
      STATUTS_CONCERNANT_FUSION.includes(d.statut) ? api.getFusion(idCharge) : Promise.resolve(null),
      STATUTS_CONCERNANT_ANNULATION.includes(d.statut) ? api.getAnnulation(idCharge) : Promise.resolve(null),
    ]);
    if (perime()) return;
    setEvents(ev);
    setRetourInfo(ret);
    setAffectation(aff);
    setPassation(pas);
    setPresences(pres);
    setRecherches(rech);
    setMessages(msgs);
    setParticipants(part);
    setFak(fakKit);
    setFakFeedback(retours);
    setFusion(fus);
    setAnnulation(annul);
  }

  useEffect(() => {
    let annule = false;
    // Changer de fiche : on repart d'une page vide. Sinon, si la nouvelle fiche
    // est refusée (« introuvable »), l'ancienne resterait affichée sous le message.
    // Les messages et fenêtres propres à la fiche précédente disparaissent aussi.
    setPropositionFaite(null); setProposerOuvert(false); setFicheFraOuverte(false);
    // Une fenêtre d'action restée ouverte et les saisies de la fiche précédente disparaissent aussi : sinon la
    // fenêtre enverrait son action sur la NOUVELLE fiche (les actions partent sur l'id courant).
    setModal(null); setActionEnCours(null); setErreurAction(null); setFeedbackTexte('');
    setMRemplacantId(null); setMStagiaire(false); setMGarantId(null); setMRemplacantPrefillNom(null);
    setMMotif(''); setMAtelierCible(null); setMLieuHoraire(''); setMFacturation('aucune');
    setReponseForms({}); setDetailOverride({}); setMessageVisible({}); setFormVisible({});
    setDemande(null);
    setLoading(true);
    charger()
      .catch((err: unknown) => { if (!annule) setErreurChargement(err instanceof Error ? err.message : 'Impossible de charger la demande.'); })
      .finally(() => { if (!annule) setLoading(false); });
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function recharger() {
    const idRecharge = id;
    try { await charger(); } catch (err) {
      // L'échec d'un rechargement de l'ancienne fiche ne s'affiche pas sur la nouvelle.
      if (idAffiche.current === idRecharge) setErreurChargement(err instanceof Error ? err.message : 'Impossible de recharger la demande.');
    }
  }

  /** « C'est publié » sur la carte « Prochaine étape » : le message Slack est parti à la main (docs/DECISIONS.md §24). */
  async function marquerMessagePublie(messageId: number) {
    setMarquageEnCours(true);
    setErreurAction(null);
    try {
      await api.marquerMessageEnvoye(messageId);
      annoncerMiseAJourMessages();
      await recharger();
      setAnnonceMessage('Message marqué comme publié.');
      carteEtape.current?.focus();   // le bouton « C'est publié » a disparu : on garde le focus sur la carte
    } catch (err) {
      setErreurAction(err instanceof Error ? err.message : "L'enregistrement n'a pas abouti. Réessayez.");
      void recharger();   // l'état affiché a peut-être changé entre-temps (quelqu'un d'autre l'a traité, la demande a changé)
    } finally {
      setMarquageEnCours(false);
    }
  }

  // ── Panneau d'actions ────────────────────────────────────────────────────
  const [modal, setModal] = useState<ModalState>(null);
  const [actionEnCours, setActionEnCours] = useState<Action | null>(null);
  const [erreurAction, setErreurAction] = useState<string | null>(null);

  // Échap ferme la modale — seul raccourci clavier de fermeture : le clic sur
  // l'overlay N'EN EST PLUS UN (voir le rendu de la modale plus bas) pour ne
  // jamais effacer une saisie (motif, sélection…) par un clic à côté malvoulu.
  useEffect(() => {
    if (!modal) return;
    const surEchap = (e: KeyboardEvent) => { if (e.key === 'Escape') setModal(null); };
    document.addEventListener('keydown', surEchap);
    return () => document.removeEventListener('keydown', surEchap);
  }, [modal]);

  const [mRemplacantId, setMRemplacantId] = useState<number | null>(null);
  const [mStagiaire, setMStagiaire] = useState(false);
  const [mGarantId, setMGarantId] = useState<number | null>(null);
  // Nom pré-rempli quand la modale est ouverte depuis une réponse « oui »
  // (affiche un rappel « depuis la réponse de … » dans la modale). null sinon.
  const [mRemplacantPrefillNom, setMRemplacantPrefillNom] = useState<string | null>(null);
  const [mMotif, setMMotif] = useState('');
  const [mAtelierCible, setMAtelierCible] = useState<number | null>(null);
  const [mLieuHoraire, setMLieuHoraire] = useState('');
  const [mFacturation, setMFacturation] = useState<'heures_supp' | 'facture_externe' | 'aucune'>('aucune');
  const [ateliersCible, setAteliersCible] = useState<Atelier[]>([]);

  useEffect(() => {
    if (modal?.type === 'fusion') {
      api.getAteliers({ actif: true, reel: true }).then(setAteliersCible).catch(() => {});
    }
  }, [modal]);

  function ouvrirModal(action: Action) {
    setErreurAction(null);
    if (action === 'saisir_remplacant') {
      setMRemplacantId(null); setMStagiaire(false); setMGarantId(null); setMRemplacantPrefillNom(null);
      setModal({ type: 'remplacant' });
    } else if (action === 'annuler_atelier' || action === 'refuser' || action === 'annuler_demande') {
      setMMotif('');
      setModal({ type: 'motif', action, label: 'Motif' });
    } else if (action === 'refuser_absence_rh') {
      setMMotif('');
      setModal({ type: 'motif', action, label: 'Motif du refus (communiqué à la personne absente et au/à la remplaçant·e)' });
    } else if (action === 'declencher_urgence' || action === 'urgence_fusion' || action === 'urgence_maintien' || action === 'urgence_annulation') {
      setMMotif('');
      setModal({ type: 'motif', action, label: 'Commentaire' });
    } else if (action === 'demander_fusion' || action === 'confirmer_fusion') {
      setMAtelierCible(fusion?.atelier_cible_id ?? null);
      setMLieuHoraire(fusion?.lieu_horaire ?? '');
      setModal({ type: 'fusion', action });
    } else if (action === 'cloturer') {
      setMFacturation(demande?.facturation ?? 'aucune');
      setModal({ type: 'cloture' });
    } else {
      // Actions sans formulaire propre (relancer, valider, clôturer une
      // étape…) : une pop-up de confirmation minimale avant exécution — pour
      // qu'un clic n'applique jamais une action irrécupérable sans validation.
      setModal({ type: 'confirmer', action });
    }
  }

  async function executer(action: Action, details?: Record<string, unknown>) {
    setActionEnCours(action);
    setErreurAction(null);
    try {
      await api.appliquerAction(id, action, details);
      // La personne désignée qui décline n'a plus accès à la fiche : retour au
      // tableau de bord plutôt qu'une fiche périmée (« Demande introuvable »).
      if (action === 'refuser_remplacement' && !peutGerer && demande?.ma_personne_id != null
          && demande.ma_personne_id !== demande.absent_personne_id) {
        setModal(null);
        naviguer('tableau');
        return;
      }
      await recharger();
      setModal(null);
    } catch (err) {
      setErreurAction(err instanceof Error ? err.message : 'Une erreur est survenue.');
    } finally {
      setActionEnCours(null);
    }
  }

  async function soumettreRemplacant() {
    if (mRemplacantId == null || (mStagiaire && mGarantId == null)) { setErreurAction('Merci de compléter le formulaire.'); return; }
    await executer('saisir_remplacant', { remplacant_personne_id: mRemplacantId, stagiaire: mStagiaire, garant_personne_id: mStagiaire ? mGarantId : undefined });
  }

  async function soumettreMotif() {
    if (!modal || modal.type !== 'motif') return;
    if (!mMotif.trim()) { setErreurAction('Merci de préciser un motif.'); return; }
    const action = modal.action;
    setActionEnCours(action);
    setErreurAction(null);
    try {
      if (action === 'annuler_atelier' || action === 'refuser') {
        await api.updateAnnulation(id, { motif: mMotif.trim() });
      }
      await api.appliquerAction(id, action, { motif: mMotif.trim() });
      await recharger();
      setModal(null);
    } catch (err) {
      setErreurAction(err instanceof Error ? err.message : 'Une erreur est survenue.');
    } finally {
      setActionEnCours(null);
    }
  }

  async function soumettreFusion() {
    if (!modal || modal.type !== 'fusion') return;
    if (mAtelierCible == null) { setErreurAction("Merci de sélectionner l'atelier cible."); return; }
    const action = modal.action;
    setActionEnCours(action);
    setErreurAction(null);
    try {
      await api.updateFusion(id, {
        atelier_cible_id: mAtelierCible,
        lieu_horaire: mLieuHoraire.trim() || undefined,
        ...(action === 'confirmer_fusion' ? { confirmee_at: new Date().toISOString() } : {}),
      });
      await api.appliquerAction(id, action, { atelier_cible_id: mAtelierCible, lieu_horaire: mLieuHoraire.trim() || undefined });
      await recharger();
      setModal(null);
    } catch (err) {
      setErreurAction(err instanceof Error ? err.message : 'Une erreur est survenue.');
    } finally {
      setActionEnCours(null);
    }
  }

  async function soumettreCloture() {
    setActionEnCours('cloturer');
    setErreurAction(null);
    try {
      await api.updateDemande(id, { facturation: mFacturation });
      await api.appliquerAction(id, 'cloturer');
      await recharger();
      setModal(null);
    } catch (err) {
      setErreurAction(err instanceof Error ? err.message : 'Une erreur est survenue.');
    } finally {
      setActionEnCours(null);
    }
  }

  function soumettreModal() {
    if (!modal) return;
    if (modal.type === 'remplacant') void soumettreRemplacant();
    else if (modal.type === 'motif') void soumettreMotif();
    else if (modal.type === 'fusion') void soumettreFusion();
    else if (modal.type === 'cloture') void soumettreCloture();
    else if (modal.type === 'confirmer_refus') void executer('refuser_remplacement');
    else if (modal.type === 'tipee_completer') void soumettreTipeeACompleter();
    else if (modal.type === 'confirmer') void executer(modal.action);
  }

  // Envoi d'une demande créée automatiquement à partir d'une absence Tipee :
  // la fenêtre rappelle d'où elle vient (voir OrigineBadge) avant de l'envoyer.
  const envoiDemandeTipee = modal?.type === 'confirmer' && modal.action === 'soumettre' && demande?.source === 'tipee';
  const envoiMaDemande = envoiDemandeTipee && demande?.ma_personne_id != null && demande.ma_personne_id === demande.absent_personne_id;

  // Le bouton de validation dit CE QU'IL FAIT (« Oui, annuler l'atelier »), et la
  // sortie s'appelle « Retour » : « Annuler / Confirmer » se confondaient (annuler
  // l'atelier en croyant fermer la fenêtre).
  function libelleValider(m: NonNullable<ModalState>): string {
    if (envoiDemandeTipee) return envoiMaDemande ? 'Envoyer ma demande' : 'Envoyer la demande';
    if (m.type === 'remplacant') return 'Désigner';
    if (m.type === 'cloture') return 'Clôturer';
    if (m.type === 'confirmer_refus') return 'Oui, décliner';
    if (m.type === 'tipee_completer') return 'Prévenir la personne';
    const l = LIBELLES_ACTION[m.action];
    return ACTIONS_DANGER.includes(m.action) ? `Oui, ${l.charAt(0).toLowerCase()}${l.slice(1)}` : l;
  }
  function actionDangereuse(m: NonNullable<ModalState>): boolean {
    if (m.type === 'confirmer_refus') return true;
    return (m.type === 'confirmer' || m.type === 'motif' || m.type === 'fusion') && ACTIONS_DANGER.includes(m.action);
  }

  function titreModal(m: NonNullable<ModalState>): string {
    if (envoiDemandeTipee) {
      return envoiMaDemande ? 'Envoyer votre demande de remplacement ?' : `Envoyer la demande de ${demande?.absent_nom ?? 'la personne absente'} ?`;
    }
    if (m.type === 'remplacant') return LIBELLES_ACTION.saisir_remplacant;
    if (m.type === 'cloture') return LIBELLES_ACTION.cloturer;
    if (m.type === 'confirmer_refus') return LIBELLES_ACTION.refuser_remplacement;
    if (m.type === 'tipee_completer') return 'Demande Tipee à compléter';
    return LIBELLES_ACTION[m.action];
  }

  // RH : la demande Tipee doit être complétée par la personne absente (BPMN
  // « Demande d'infos manquantes ») — la demande reste en attente de Tipee.
  async function soumettreTipeeACompleter() {
    if (!mMotif.trim()) { setErreurAction('Indiquez ce qui manque dans la demande Tipee.'); return; }
    setErreurAction(null);
    try {
      await api.tipeeACompleter(id, mMotif.trim());
      await recharger();
      setModal(null);
    } catch (err) {
      setErreurAction(err instanceof Error ? err.message : 'Une erreur est survenue.');
    }
  }

  async function marquerTipeeVu() {
    try { await api.updateDemande(id, { tipee_rappel_vu: true }); await recharger(); } catch (err) { setErreurAction(err instanceof Error ? err.message : 'Action impossible.'); }
  }

  // URL de l'instance Tipee (réglage admin) — pour le lien « Ouvrir Tipee ».
  const [tipeeUrl, setTipeeUrl] = useState('');
  useEffect(() => { api.getSettings().then((s) => setTipeeUrl(lienTipee(s['tipee.instance_url']))).catch(() => {}); }, []);

  // ── Recherche : réponses ────────────────────────────────────────────────
  const [reponseForms, setReponseForms] = useState<Record<number, ReponseForm>>({});
  // Replis de l'onglet Recherche (par round) : détail ouvert/fermé, message
  // visible, formulaire d'ajout visible. Chaque map ne contient QUE les
  // surcharges explicites de l'utilisateur ; sinon on retombe sur un défaut
  // calculé (cf. bloc de rendu). Même motif que reponseForms.
  const [detailOverride, setDetailOverride] = useState<Record<number, boolean>>({});
  const [messageVisible, setMessageVisible] = useState<Record<number, boolean>>({});
  const [formVisible, setFormVisible] = useState<Record<number, boolean>>({});
  function formPourRound(rechercheId: number): ReponseForm {
    return reponseForms[rechercheId] ?? { personneId: null, positive: true, commentaire: '' };
  }
  function majForm(rechercheId: number, patch: Partial<ReponseForm>) {
    setReponseForms((prev) => ({ ...prev, [rechercheId]: { ...formPourRound(rechercheId), ...patch } }));
  }
  // Ouvre la modale « Saisir le remplaçant » déjà EXISTANTE, mais pré-remplie
  // avec la personne d'une réponse « oui » — évite de retaper le nom ailleurs
  // (le bug de double saisie). N'écrit rien : c'est la modale qui applique
  // l'action, comme depuis « Prochaine étape ».
  function assignerDepuisReponse(personneId: number, nom: string) {
    setErreurAction(null);
    setMRemplacantId(personneId);
    setMStagiaire(false);
    setMGarantId(null);
    setMRemplacantPrefillNom(nom);
    setModal({ type: 'remplacant' });
  }
  async function ajouterReponse(rechercheId: number) {
    const f = formPourRound(rechercheId);
    try {
      await api.ajouterReponse(id, rechercheId, { personne_id: f.personneId ?? undefined, positive: f.positive, commentaire: f.commentaire.trim() || undefined });
      setReponseForms((prev) => { const next = { ...prev }; delete next[rechercheId]; return next; });
      await recharger();
    } catch (err) {
      setErreurAction(err instanceof Error ? err.message : "Impossible d'enregistrer la réponse.");
    }
  }
  async function copier(texte: string) {
    try { await navigator.clipboard.writeText(texte); } catch { /* presse-papier indisponible */ }
  }

  // ── Passation / Présences ────────────────────────────────────────────────
  async function majPassation(patch: Record<string, unknown>) {
    try { await api.updatePassation(id, patch); await recharger(); } catch (err) { setErreurAction(err instanceof Error ? err.message : 'Action impossible.'); }
  }
  async function majPresences(patch: Partial<ListePresence>) {
    try { await api.updatePresences(id, patch); await recharger(); } catch (err) { setErreurAction(err instanceof Error ? err.message : 'Action impossible.'); }
  }

  // ── FRA ──────────────────────────────────────────────────────────────────
  const [feedbackTexte, setFeedbackTexte] = useState('');
  async function ajouterFeedback() {
    if (!fak || !feedbackTexte.trim()) return;
    try {
      await api.addFakFeedback(fak.id, { contenu: feedbackTexte.trim(), demande_id: id });
      setFeedbackTexte('');
      await recharger();
    } catch (err) {
      setErreurAction(err instanceof Error ? err.message : "Impossible d'ajouter le retour.");
    }
  }

  if (loading) {
    return <div className="page"><div className="flex justify-center p-24"><div className="spinner" /></div></div>;
  }
  if (!demande) {
    return (
      <div className="page">
        <div className="empty-state">
          <div className="empty-state-title">Demande introuvable</div>
          <div className="empty-state-desc">
            {erreurChargement && erreurChargement !== 'Demande introuvable'
              ? erreurChargement
              : "Cette demande n'existe pas, ou vous n'y avez pas accès."}
          </div>
          <button type="button" className="btn btn-accent mt-16" onClick={() => naviguer('tableau')}>Retour au tableau de bord</button>
        </div>
      </div>
    );
  }

  // Lien du message Slack ouvert alors que la recherche est terminée : on le dit simplement (le
  // serveur ne renvoie alors que l'atelier et la date, rien d'autre).
  if (demande.acces === 'close') {
    const pourvue = demande.issue === 'pourvue';
    return (
      <div className="page">
        <div className="card" style={{ display: 'grid', gap: 8 }}>
          <div className="font-medium" style={{ fontSize: 'var(--t-16, 16px)' }}>
            {pourvue ? 'Un·e remplaçant·e a été trouvé·e, merci !' : 'Cette recherche est terminée.'}
          </div>
          <div className="text-sm text-ink-2">
            {demande.atelier_nom} · {formatDateLongue(demande.date_atelier)}.{' '}
            {pourvue ? "Il n'y a plus rien à faire pour cette séance." : "Il n'y a plus de remplaçant·e à trouver pour cette séance."}
          </div>
          <div>
            <button type="button" className="btn btn-accent" onClick={() => naviguer('tableau', undefined, { vue: 'collegues' })}>
              Voir les demandes de vos collègues
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Collègue (périmètre « Demandes des collègues ») : fiche réduite au minimum utile pour se
  // proposer — pas d'onglets, une seule action : « Je me propose ».
  if (demande.acces === 'limite') {
    const { dow, jour, mois } = partiesDate(demande.date_atelier);
    const horaire = horaireCourt(demande.atelier_heure_debut, demande.atelier_heure_fin);
    const passee = String(demande.date_atelier).slice(0, 10) < jourLocalIso();
    const peutProposer = !passee && (demande.actions_possibles ?? []).includes('saisir_remplacant');
    return (
      <div className="page">
        <div className="card" style={{ display: 'grid', gap: 12 }}>
          <div className="flex items-center gap-16">
            <div style={{ flexShrink: 0, width: 60, height: 60, borderRadius: 'var(--r2)', border: '2px solid var(--ink-3)', color: 'var(--ink-3)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 10, textTransform: 'uppercase', fontWeight: 700 }}>{dow}</span>
              <span style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{jour}</span>
              <span style={{ fontSize: 10, textTransform: 'uppercase', fontWeight: 600 }}>{mois}</span>
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="font-medium" style={{ fontSize: 'var(--t-16, 16px)' }}>{demande.atelier_nom}</div>
              <div className="text-sm text-ink-2">
                {[demande.pole_nom, demande.atelier_lieu, horaire].filter(Boolean).join(' · ')}
              </div>
              {demande.absent_nom && (
                <div className="text-sm text-ink-3">
                  {['urgence_cascade', 'fusion_demandee', 'evaluation_annulation'].includes(demande.statut)
                    ? "Pris en charge par l'équipe gestion"
                    : `${demande.absent_nom} cherche un·e remplaçant·e`}
                </div>
              )}
              {demande.pense_a_vous && <PenseAVous de={demande.pense_a_vous.de} />}
            </div>
            <div style={{ marginLeft: 'auto' }}><StatutBadge statut={demande.statut} libelle={meta?.statuts[demande.statut]} compact /></div>
          </div>
          {aFra(demande) && (
            <div>
              <button type="button" className="rech-toggle-link" onClick={() => setFicheFraOuverte(true)}>Consulter la fiche relais (FRA)</button>
              {demande.fak_echelle && <span className="text-ink-3 text-sm"> · {demande.fak_echelle}</span>}
            </div>
          )}
          {demande.conflits?.anime && (
            <div className="proposer-alerte" style={{ marginBottom: 0 }}>⚠ Vous animez « {demande.conflits.anime} » à cette heure.</div>
          )}
          {demande.conflits?.absente && (
            <div className="proposer-alerte" style={{ marginBottom: 0 }}>⚠ Vous êtes absent·e ce jour-là.</div>
          )}
          <div className="flex gap-12" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
            {peutProposer && (
              <button type="button" className="btn btn-accent" disabled={maPersonne == null} onClick={() => setProposerOuvert(true)}>
                Je me propose
              </button>
            )}
            {passee && <span className="text-sm text-ink-3">Cette séance est passée.</span>}
            {!passee && !peutProposer && <span className="text-sm text-ink-3">Cette demande est prise en charge par l'équipe gestion.</span>}
            <button type="button" className="rech-toggle-link" onClick={() => naviguer('tableau', undefined, { vue: 'collegues' })}>
              Voir toutes les demandes de vos collègues
            </button>
          </div>
        </div>
        {proposerOuvert && maPersonne != null && (
          <ProposerRemplacementModal
            demande={demande} maPersonneId={maPersonne.id}
            onClose={() => setProposerOuvert(false)}
            onFini={(r) => {
              if (r !== 'echec') { setProposerOuvert(false); setPropositionFaite(r); }
              void recharger();
            }}
          />
        )}
        {ficheFraOuverte && (
          <FicheFakOverlay atelierId={demande.atelier_id} demandeId={demande.id} onClose={() => setFicheFraOuverte(false)} />
        )}
      </div>
    );
  }

  // Un·e intervenant·e simple ne peut désigner un·e remplaçant·e que sur SA
  // propre demande (le serveur applique la vraie barrière). On masque donc les
  // boutons de saisie/assignation sur la demande d'autrui. Identité non
  // déterminable côté client (compte lié par e-mail) → on laisse le serveur
  // trancher. Partagé entre la carte « Prochaine étape » et l'onglet Recherche.
  // Fourni par GET /demandes/:id (le compte du kit d'auth n'expose pas sa fiche
  // personne) : sans cela, ni le/la remplaçant·e ni l'absent·e n'étaient reconnu·e·s.
  const monPersonneId = demande.ma_personne_id ?? null;
  // La personne désignée voit « À vous de confirmer » (et non « En attente de
  // confirmation du/de la remplaçant·e », écrit pour les autres).
  const estRemplacantDesigne = affectation != null && monPersonneId != null
    && monPersonneId === affectation.remplacant_personne_id;
  const peutSaisirRemplacant = peutGerer || monPersonneId == null || monPersonneId === demande.absent_personne_id;
  // La recherche élargie (e-mails au vivier externe) est l'affaire de la gestion.
  const libelleCanal = (canal: keyof typeof LIBELLES_CANAL): string =>
    !peutGerer && canal === 'email_externe' ? "Recherche élargie par l'équipe gestion" : LIBELLES_CANAL[canal];

  return (
    <div className="page">
      {propositionFaite && (
        <div className="proposition-confirmee">
          <div className="proposition-confirmee-titre">
            {propositionFaite === 'ok' ? '✓ Proposition enregistrée' : "Vous êtes désigné·e, mais la confirmation n'a pas abouti"}
          </div>
          <div className="proposition-confirmee-sub">
            {propositionFaite === 'ok'
              ? "En attente de validation par l'équipe gestion. Vous serez prévenu·e de la suite."
              : 'Confirmez-la depuis la carte « Prochaine étape » ci-dessous.'}
          </div>
        </div>
      )}
      {(() => {
        const { dow, jour, mois } = partiesDate(demande.date_atelier);
        // La couleur d'urgence n'a de sens QUE tant qu'un remplacement reste
        // à trouver (méta-états à_traiter/recherche/urgence) — au-delà
        // (remplaçant·e déjà trouvé·e, passation, terminé…), afficher du
        // rouge/orange laisserait croire à tort qu'il reste une urgence.
        const metaEtat = STATUT_META[demande.statut];
        const encoreARisque = metaEtat === 'a_traiter' || metaEtat === 'recherche' || metaEtat === 'urgence';
        const niveau = encoreARisque ? niveauUrgence(joursAvant(demande.date_atelier, new Date()), seuils) : null;
        const couleurPastille = niveau ? COULEUR_NIVEAU[niveau] : 'var(--ink-3)';
        return (
          <div className="page-header">
            <div className="flex items-center gap-16" style={{ minWidth: 0 }}>
              <div
                style={{
                  flexShrink: 0, width: 60, height: 60, borderRadius: 'var(--r2)',
                  border: `2px solid ${couleurPastille}`, color: couleurPastille,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <span style={{ fontSize: 10, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' }}>{dow}</span>
                <span style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{jour}</span>
                <span style={{ fontSize: 10, textTransform: 'uppercase', fontWeight: 600 }}>{mois}</span>
              </div>
              <div style={{ minWidth: 0 }}>
                {/* Date complète toujours visible (pas seulement en
                    infobulle au survol, invisible au doigt sur tablette). */}
                <div className="text-xs text-ink-3" style={{ textTransform: 'capitalize' }}>{formatDateLongue(demande.date_atelier)}</div>
                <div className="flex items-center gap-8">
                  <span
                    aria-hidden
                    style={{
                      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                      background: 'var(--accent)', color: '#fff',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700,
                    }}
                  >
                    {initialesDe(demande.absent_nom)}
                  </span>
                  {peutGerer && demande.absent_nom ? (
                    <button
                      type="button"
                      title={`Voir toutes les demandes de ${demande.absent_nom}`}
                      onClick={() => naviguer('tableau', undefined, { q: demande.absent_nom ?? '' })}
                      style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        fontSize: 19, fontWeight: 700, color: 'var(--accent)',
                        textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3,
                      }}
                    >
                      {demande.absent_nom}
                    </button>
                  ) : (
                    <span style={{ fontSize: 19, fontWeight: 700 }}>{demande.absent_nom}</span>
                  )}
                </div>
                <div className="flex items-center gap-8 mt-4">
                  <span className="badge badge-plain" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <PoleDot couleur={demande.pole_couleur} /> {demande.pole_nom}
                  </span>
                  <span className="font-medium text-ink-1">{demande.atelier_nom}</span>
                  {demande.atelier_lieu && <span className="text-ink-3">· {demande.atelier_lieu}</span>}
                </div>
              </div>
            </div>
            <div className="header-controls header-controls-haut">
              <TypeBadge type={demande.type} />
              <OrigineBadge source={demande.source} />
              {/* « Dates flexibles » : pour la gestion, la personne absente et l'auteur·rice (pas pour le/la remplaçant·e). */}
              {demande.dates_flexibles && (peutGerer || (monPersonneId != null && monPersonneId === demande.absent_personne_id) || (user?.id != null && user.id === demande.created_by)) && (
                <BadgeDatesFlexibles absentNom={demande.absent_nom} estLaPersonneAbsente={monPersonneId != null && monPersonneId === demande.absent_personne_id} />
              )}
              <StatutBadge
                statut={demande.statut}
                libelle={demande.statut === 'attente_confirmation_remplacant' && estRemplacantDesigne ? 'À vous de confirmer' : meta?.statuts[demande.statut]}
              />
            </div>
          </div>
        );
      })()}

      {/* Frise de progression + carte « Prochaine étape » : traduit le statut
          (petit badge cryptique) en « à quelle étape on est » et « qui doit
          agir maintenant », ET réunit au même endroit les vrais boutons
          d'action de la personne (plus de carte « Actions » décrochée dont le
          texte pouvait promettre un bouton absent). Voir components/demande/parcours.ts. */}
      {(() => {
        const phase = phaseCourante(demande.statut, demande.date_atelier);
        const info = infoBandeau(demande.statut, demande.date_atelier);
        const estAbsent = monPersonneId != null && monPersonneId === demande.absent_personne_id;
        // Textes propres à la personne qui regarde (l'absent·e cherche en premier,
        // la gestion prend le relais) — voir parcours.ts::surchargeCarte.
        const relanceDisponible = (demande.actions_possibles ?? []).includes('relancer_slack');
        const relanceAt = demande.relance_possible_at ? new Date(demande.relance_possible_at) : null;
        const relancePossibleLe = relanceAt
          ? `${relanceAt.toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' })} à ${relanceAt.getHours()} h${relanceAt.getMinutes() ? ` ${String(relanceAt.getMinutes()).padStart(2, '0')}` : ''}`
          : null;
        // Le message Slack de la recherche en cours : la carte dit ce qui s'est VRAIMENT passé (docs/DECISIONS.md §24).
        const messageSlack = ((): ContexteMessageSlack | null => {
          if (demande.statut !== 'recherche_interne_1' && demande.statut !== 'recherche_interne_2') return null;
          const slack = messages.filter((x) => x.canal === 'slack');
          const m = slack[slack.length - 1];
          if (!m) return null;
          return {
            messageId: m.id, texte: m.corps, etat: m.statut, relance: demande.statut === 'recherche_interne_2' || slack.length > 1,
            aCharge: m.a_charge_de, peutMarquer: m.peut_marquer, lanceParNom: m.lance_par_nom, quand: m.envoye_at ? quandLisible(m.envoye_at) : null,
          };
        })();
        const surcharge = surchargeCarte(demande.statut, {
          peutGerer, estAbsent, messageSlack, absentNom: demande.absent_nom ?? 'La personne absente', relanceDisponible, relancePossibleLe,
          source: demande.source, reserveGestion: demande.reserve_gestion === true,
          peutValider: (demande.actions_possibles ?? []).some((a) => a === 'valider_responsable' || a === 'valider_rh'),
          remplacantNom: affectation?.remplacant_nom ?? demande.remplacant_nom ?? null,
        });
        const acteurCarte = surcharge?.acteur ?? info.acteur;
        const couleurActeur = COULEUR_ACTEUR[acteurCarte];
        const IconeActeur = ICONE_ACTEUR[acteurCarte];
        // Confirmation du/de la remplaçant·e : la carte porte elle-même la
        // décision (« Je confirme » / « Décliner »), personnalisée selon que
        // la personne qui regarde EST le/la remplaçant·e désigné·e.
        const estConfirmation = demande.statut === 'attente_confirmation_remplacant';
        const estRemplacant = affectation != null
          && monPersonneId != null
          && monPersonneId === affectation.remplacant_personne_id;
        const enCoursConfirm = actionEnCours === 'confirmer_remplacement' || actionEnCours === 'refuser_remplacement';
        // Une séance passée ne se confirme plus (le serveur la refuse aussi).
        const seancePassee = demande.date_atelier.slice(0, 10) < new Date().toISOString().slice(0, 10);
        const titre = estConfirmation && seancePassee
          ? 'Cette séance est passée'
          : estConfirmation
          ? (estRemplacant ? 'On vous propose de remplacer cette séance' : `En attente de confirmation de ${affectation?.remplacant_nom ?? 'la personne remplaçante'}`)
          : (surcharge?.titre ?? info.titre);
        const sousTitre = estConfirmation && seancePassee
          ? "Le remplacement n'a pas été confirmé avant la date de la séance. L'équipe gestion peut classer la demande."
          : estConfirmation
          ? (estRemplacant ? `Confirmez-vous votre disponibilité le ${formatDateCourt(demande.date_atelier)} ?` : undefined)
          : (surcharge ? surcharge.sousTitre : info.sousTitre);
        // « Qui tient la balle », adapté à la personne qui regarde : « À vous
        // de jouer » quand c'est à elle d'agir, sinon on nomme l'équipe/personne
        // responsable (rendu en MAJUSCULES par .pe-eyebrow). Vide pour les états
        // terminaux (rien à ajouter à « Terminé »).
        // Les vrais boutons d'action de la personne (transitions légales,
        // calculées par le serveur selon son rôle). confirmer/refuser sont
        // rendus à part (décision personnalisée « Je confirme / Décliner »),
        // on les retire ici — mais les AUTRES actions restent (ex. annuler_demande
        // pour un·e responsable/RH pendant l'attente de confirmation).
        const actionsGeneriques = (demande.actions_possibles ?? []).filter(
          (a) => a !== 'confirmer_remplacement' && a !== 'refuser_remplacement'
            && !(a === 'saisir_remplacant' && !peutSaisirRemplacant),
        );
        const montrerConfirmation = estConfirmation && (estRemplacant || peutGerer) && !seancePassee;
        // « Console Urgence » : seulement pour qui y a accès (la personne absente y lisait « Accès réservé »).
        const montrerUrgence = demande.statut === 'urgence_cascade' && peutGerer;
        const aDesActions = montrerConfirmation || actionsGeneriques.length > 0 || montrerUrgence || !!surcharge?.messageAction;
        const quiAgit = ((): string => {
          if (surcharge) return surcharge.quiAgit;
          if (info.acteur === 'termine') return '';
          if (info.acteur === 'alerte' && demande.statut !== 'urgence_cascade') return ''; // annulée / refusée
          if (info.acteur === 'remplacant') return estRemplacant ? 'À vous de jouer' : 'En attente du/de la remplaçant·e';
          if (info.acteur === 'absent') return "Côté intervenant·e absent·e";
          // gestion (et urgence, pilotée par la gestion) : « À vous de jouer » seulement
          // si la personne a réellement un bouton — sinon on ne la fait pas croire
          // que la balle est dans son camp.
          return peutGerer && aDesActions ? 'À vous de jouer' : "Suivi par l'équipe gestion";
        })();
        const eyebrow = quiAgit ? `${info.phaseLabel} · ${quiAgit}` : info.phaseLabel;
        // Boutons de l'absent·e : ses mots à elle/lui, une seule action en avant.
        const motsAbsent = !peutGerer && estAbsent;
        const LIBELLES_ABSENT: Record<string, string> = {
          soumettre: 'Envoyer ma demande',
          lancer_recherche: 'Prévenir mes collègues sur Slack',
          relancer_slack: 'Relancer mes collègues sur Slack',
          saisir_remplacant: "J'ai trouvé un·e remplaçant·e",
        };
        return (
          <>
            {phase !== null && (
              <div className="frise" role="list" aria-label="Progression de la demande">
                {PHASES_PARCOURS.map((label, idx) => {
                  const n = idx + 1;
                  const etat = phase > PHASES_PARCOURS.length || n < phase ? 'done' : n === phase ? 'current' : 'todo';
                  return (
                    <div
                      key={label}
                      className={`frise-step ${etat}`}
                      role="listitem"
                      style={etat === 'current' ? ({ '--fs-accent': couleurActeur } as CSSProperties) : undefined}
                    >
                      <div className="frise-circle">{etat === 'done' ? '✓' : n}</div>
                      <div className="frise-lbl">{label}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {retourInfo && (phase === 7 || retourInfo.retour?.transmis_at != null) && (
              <RetourCarte
                demande={demande} info={retourInfo} estAbsent={estAbsent} peutGerer={peutGerer}
                onChange={() => void recharger()} onOuvrirPresences={() => setOnglet('presences')}
              />
            )}
            <div className="prochaine-etape" style={{ '--pe-c': couleurActeur } as CSSProperties} ref={carteEtape} tabIndex={-1}>
              <span className="sr-only" role="status">{annonceMessage}</span>
              <div className="pe-head">
                <div className="pe-ic"><IconeActeur className="icon-sm" /></div>
                <div className="pe-txt">
                  <div className="pe-eyebrow">{eyebrow}</div>
                  <div className="pe-titre">{titre}</div>
                  {sousTitre && <div className="pe-sub">{sousTitre}</div>}
                </div>
              </div>
              {aDesActions && (
                <div className="pe-actions">
                  {surcharge?.messageAction && (
                    <>
                      <BoutonCopier texte={surcharge.messageAction.texte} accent petit={false} />
                      <button type="button" className="btn" disabled={marquageEnCours} onClick={() => void marquerMessagePublie(surcharge.messageAction!.messageId)}>
                        {marquageEnCours ? <span className="spinner spinner-sm" role="status" aria-label="Enregistrement en cours" /> : surcharge.messageAction.libelleMarquer}
                      </button>
                    </>
                  )}
                  {montrerConfirmation && (
                    <>
                      <button className="btn btn-accent" disabled={enCoursConfirm} onClick={() => executer('confirmer_remplacement')}>
                        {estRemplacant ? 'Je confirme' : 'Confirmer (manuel)'}
                      </button>
                      <button className="btn btn-ghost" disabled={enCoursConfirm} onClick={() => setModal({ type: 'confirmer_refus' })}>Décliner</button>
                    </>
                  )}
                  {actionsGeneriques.map((action) => {
                    // Pour l'absent·e : une seule action en avant (celle de l'étape),
                    // les autres en neutre — plus de contradiction « rien à faire »
                    // à côté d'un bouton principal.
                    const classe = motsAbsent
                      ? (action === surcharge?.actionPrincipale ? 'btn-accent' : '')
                      : surcharge?.toutNeutre
                        ? (ACTIONS_DANGER.includes(action) ? 'btn-danger' : '')
                        : ACTIONS_DANGER.includes(action) ? 'btn-danger' : ACTIONS_NEUTRES.includes(action) ? 'btn-ghost' : 'btn-accent';
                    return (
                      <button
                        key={action}
                        type="button"
                        className={`btn ${classe}`}
                        disabled={actionEnCours != null}
                        onClick={() => ouvrirModal(action)}
                      >
                        {actionEnCours === action ? <span className="spinner spinner-sm" /> : (motsAbsent && LIBELLES_ABSENT[action]) || surcharge?.libellesActions?.[action] || LIBELLES_ACTION[action]}
                      </button>
                    );
                  })}
                  {montrerUrgence && (
                    <button type="button" className="btn" onClick={() => naviguer('urgence', demande.id)}>Console Urgence</button>
                  )}
                </div>
              )}
            </div>
          </>
        );
      })()}

      {/* Erreur d'une action lancée depuis la carte ci-dessus (hors modale, qui
          affiche la sienne) — placée juste dessous, près des boutons. */}
      {erreurAction && !modal && alerteErreur(erreurAction)}

      {erreurChargement && alerteErreur(erreurChargement)}

      {/* Le rappel n'a de sens que pour la personne absente (« VOTRE demande de
          congé ») ; la gestion voit un rappel à son nom, jamais « votre ». */}
      {!demande.tipee_rappel_vu && (peutGerer || monPersonneId === demande.absent_personne_id) && (
        <div className="card flex items-center justify-between gap-12" style={{ background: 'var(--warn-dim)', borderColor: 'var(--warn)' }}>
          <span className="text-ink-1">
            {peutGerer && monPersonneId !== demande.absent_personne_id
              ? `Rappel : ${demande.absent_nom ?? 'la personne absente'} doit faire sa demande de congé dans Tipee.`
              : BANNIERE_TIPEE}
          </span>
          <div className="flex gap-8 flex-wrap" style={{ flexShrink: 0 }}>
            {tipeeUrl && (
              <a className="btn btn-sm" href={tipeeUrl} target="_blank" rel="noreferrer">
                Ouvrir Tipee <ExternalLink className="icon-sm" />
              </a>
            )}
            <button className="btn btn-sm" onClick={marquerTipeeVu}>
              {peutGerer && monPersonneId !== demande.absent_personne_id ? 'Demande Tipee faite' : "J'ai fait ma demande dans Tipee"}
            </button>
          </div>
        </div>
      )}

      {demande.statut === 'attente_tipee' && (
        <div className="card flex items-center justify-between gap-12" style={{ background: 'var(--warn-dim)', borderColor: 'var(--warn)' }}>
          <span className="text-ink-1">
            Le remplacement est validé par la responsable. Il ne manque plus que la validation de l'absence par les
            RH dans Tipee : dès qu'elle y apparaît, la passation s'ouvre d'elle-même.
          </span>
          <div className="flex gap-8 flex-wrap" style={{ flexShrink: 0 }}>
            {tipeeUrl && (
              <a className="btn btn-sm" href={tipeeUrl} target="_blank" rel="noreferrer">
                Ouvrir Tipee <ExternalLink className="icon-sm" />
              </a>
            )}
            {estRh && (
              <button className="btn btn-sm" onClick={() => { setMMotif(''); setErreurAction(null); setModal({ type: 'tipee_completer' }); }}>
                À compléter dans Tipee
              </button>
            )}
          </div>
        </div>
      )}

      {demande.hors_solde && peutGerer && (
        <div className="card text-sm" style={{ borderColor: 'var(--warn)' }}>
          <strong>Vacances hors solde.</strong> La personne a été prévenue que son solde de vacances Tipee ne couvrait
          pas cette absence, et a choisi de continuer.
        </div>
      )}

      {affectation && (
        <div className="card">
          <div className="card-title mb-8">Remplaçant·e</div>
          <div className="text-sm text-ink-2">
            {affectation.remplacant_nom}{affectation.stagiaire ? ' (stagiaire' + (affectation.garant_nom ? ` · garant·e : ${affectation.garant_nom}` : '') + ')' : ''}
          </div>
          <div className="text-xs text-ink-3 mt-4">
            {demande.statut === 'maintenue' ? "Désigné·e en urgence — atelier maintenu, sans validation ni passation." : <>
            Validation responsable : {affectation.valide_responsable_at ? formatDateHeure(affectation.valide_responsable_at) : 'en attente'}
            {' · '}Contrôle RH (portail) : {affectation.valide_rh_at ? formatDateHeure(affectation.valide_rh_at) : 'en attente'}
            {' · '}Absence validée dans Tipee : {demande.tipee_valide_at ? formatDateHeure(demande.tipee_valide_at) : 'en attente'}
            </>}
          </div>
        </div>
      )}

      {demande.cascade_etapes && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Cascade d'urgence</div>
            {peutGerer && <button type="button" className="btn btn-sm" onClick={() => naviguer('urgence', demande.id)}>Console Urgence</button>}
          </div>
          <div className="cascade-liste">
            {demande.cascade_etapes.map((e) => (
              <div key={e.cle} className={`cascade-etape ${e.statut}`}>
                <strong>{e.libelle}</strong>
                {e.commentaire && <span className="text-ink-3">{e.commentaire}</span>}
                {e.horodatage && <span className="text-ink-3 ml-auto">{formatDateHeure(e.horodatage)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {annulation?.motif && (
        <div className="card">
          <div className="card-title mb-8">Annulation</div>
          <div className="text-sm text-ink-2">{annulation.motif}</div>
        </div>
      )}

      <div className="tabs">
        {ONGLETS.map((o) => (
          <button key={o.cle} type="button" className={`tab ${onglet === o.cle ? 'active' : ''}`} onClick={() => setOnglet(o.cle)}>{o.libelle}</button>
        ))}
      </div>

      {onglet === 'recherche' && demande.prevenus && demande.prevenus.length > 0 && <PrevenusPersonnellement prevenus={demande.prevenus} rechercheLancee={!['brouillon', 'soumise'].includes(demande.statut)} />}

      {onglet === 'recherche' && (recherches.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">Aucune recherche lancée</div>
          <div className="empty-state-desc">
            {(() => {
              const peutLancer = (demande.actions_possibles ?? []).includes('lancer_recherche');
              const metaE = STATUT_META[demande.statut];
              if (peutLancer) return 'Lancez la recherche depuis la carte « Prochaine étape » ci-dessus.';
              if (metaE === 'termine' || metaE === 'sans_suite') return "Aucune recherche de remplaçant·e n'a été nécessaire pour cette demande.";
              if (demande.statut === 'brouillon') return 'La recherche démarrera une fois la demande envoyée.';
              if (affectation != null) return 'Le/la remplaçant·e a été désigné·e directement, sans recherche.';
              if (demande.statut === 'urgence_cascade') return peutGerer ? 'Recherche menée par téléphone : voir la cascade d’urgence ci-dessous.' : "L'équipe gestion cherche par téléphone (urgence).";
              return peutGerer
                ? `${demande.absent_nom ?? 'La personne absente'} n'a pas encore lancé la recherche.`
                : "La recherche n'a pas encore commencé. Dès qu'elle démarre, vous la suivrez ici.";
            })()}
          </div>
        </div>
      ) : (() => {
        // ── État global de la recherche (calculé côté client, aucun appel) ──
        // saisir_remplacant présent dans les actions ⇒ on est bien dans un
        // statut où assigner a un sens ; peutSaisirRemplacant ⇒ c'est bien sa
        // demande (ou un rôle de gestion) — sinon on masque « Assigner » comme
        // « J'ai trouvé… ». Le serveur reste seul juge (barrière réelle).
        const peutAssigner = peutSaisirRemplacant && (demande.actions_possibles ?? []).includes('saisir_remplacant');
        const dejaAssigne = affectation?.remplacant_personne_id ?? null;
        // Candidat·e·s = réponses « oui » avec une personne identifiée, non
        // déjà assignée, dédupliquées.
        const vus = new Set<number>();
        const candidats: { personneId: number; nom: string }[] = [];
        for (const r of recherches) {
          for (const rep of r.reponses ?? []) {
            if (rep.positive && rep.personne_id != null && rep.personne_id !== dejaAssigne && !vus.has(rep.personne_id)) {
              vus.add(rep.personne_id);
              candidats.push({ personneId: rep.personne_id, nom: rep.personne_nom ?? 'Personne' });
            }
          }
        }
        // Dernier round par ordre chronologique (recherches est trié par
        // envoye_at côté serveur), quel que soit son état clos/ouvert : un
        // round « clos » n'est pas forcément suivi d'un round plus récent
        // (ex. passer_externe n'referme pas le round précédent), donc se
        // limiter aux rounds ouverts pouvait laisser passer un vrai retard.
        const dernierRound = recherches[recherches.length - 1];
        const enRetard = dernierRound.echeance ? classeEcheance(dernierRound.echeance) === 'echeance-ratee' : false;
        const actionRelance = (demande.actions_possibles ?? []).find((a) => a === 'relancer_slack' || a === 'passer_externe');

        // ── Variante de la bannière ──
        let rbCouleur = 'var(--ok)';
        let rbIcone = CheckCircle2;
        let rbEyebrow = '';
        let rbTitre = '';
        let rbSub: string | undefined;
        let rbAction: ReactElement | null = null;
        // Déjà assigné·e ET plus en phase de recherche (saisir indisponible) :
        // on ne propose plus de candidat, on rappelle juste qui a été retenu.
        if (affectation && !peutAssigner) {
          rbEyebrow = 'Remplaçant·e retenu·e';
          rbTitre = affectation.remplacant_nom ?? 'Remplaçant·e assigné·e';
          rbSub = 'Suivez les étapes suivantes plus haut dans « Prochaine étape ».';
        } else if (candidats.length >= 1) {
          rbEyebrow = 'Candidat·e trouvé·e';
          if (candidats.length === 1) {
            rbTitre = `${candidats[0].nom} a répondu « oui »`;
            if (peutAssigner) {
              rbSub = 'Assignez cette personne pour passer à la confirmation.';
              rbAction = <button type="button" className="btn btn-ok" onClick={() => assignerDepuisReponse(candidats[0].personneId, candidats[0].nom)}>Assigner {candidats[0].nom}</button>;
            } else {
              rbSub = 'Réponse positive enregistrée.';
            }
          } else {
            rbTitre = `${candidats.length} personnes disponibles`;
            rbSub = peutAssigner ? 'Choisissez qui assigner dans les étapes ci-dessous.' : candidats.map((c) => c.nom).join(', ');
          }
        } else if (enRetard) {
          rbCouleur = 'var(--err)';
          rbIcone = AlertTriangle;
          rbEyebrow = 'Échéance dépassée';
          rbTitre = `${libelleCanal(dernierRound.canal)} — délai passé, aucune réponse positive`;
          rbSub = actionRelance
            ? undefined
            : (peutGerer
              ? ((demande.actions_possibles ?? []).includes('passer_externe')
                ? 'Élargissez la recherche depuis la carte « Prochaine étape » ci-dessus.'
                : 'Aucune relance possible à ce stade : suivez la carte « Prochaine étape » ci-dessus.')
              : "L'équipe gestion suit votre recherche et vous aide si besoin.");
          if (actionRelance) {
            rbAction = <button type="button" className="btn btn-danger" onClick={() => ouvrirModal(actionRelance)}>{LIBELLES_ACTION[actionRelance]}</button>;
          }
        } else {
          rbCouleur = 'var(--accent)';
          rbIcone = Clock;
          rbEyebrow = 'Recherche en cours';
          rbTitre = 'En attente de réponses';
          rbSub = dernierRound.echeance ? `Échéance ${formatDateCourt(dernierRound.echeance)}.` : undefined;
          // Le message du tour n'est pas parti : on n'attend pas encore de réponses, on le dit (docs/DECISIONS.md §24).
          const messageDuTour = messages.filter((x) => x.canal === 'slack' && x.recherche_id === dernierRound.id).pop();
          if (messageDuTour && (messageDuTour.statut === 'a_envoyer' || messageDuTour.statut === 'erreur')) {
            rbCouleur = 'var(--warn)';
            rbEyebrow = 'Message pas encore parti';
            rbTitre = messageDuTour.statut === 'erreur' ? "Le message Slack n'est pas parti" : "Le message Slack n'est pas encore publié";
            rbSub = 'Vos collègues ne l’ont pas encore reçu : voir le message ci-dessous.';
          }
        }
        const RbIcone = rbIcone;

        return (
          <>
            <div className="rech-banniere" style={{ '--rb-c': rbCouleur } as CSSProperties}>
              <div className="rech-banniere-ic"><RbIcone className="icon-sm" /></div>
              <div className="rech-banniere-txt">
                <div className="rech-banniere-eyebrow">{rbEyebrow}</div>
                <div className="rech-banniere-titre">{rbTitre}</div>
                {rbSub && <div className="rech-banniere-sub">{rbSub}</div>}
              </div>
              {rbAction && <div className="rech-banniere-actions">{rbAction}</div>}
            </div>

            {recherches.map((r, i) => {
              const reps = r.reponses ?? [];
              const aPositive = reps.some((x) => x.positive);
              // Vert plein UNIQUEMENT si une réponse positive est exploitable
              // (personne identifiée, pas déjà assignée) — même filtre que
              // « candidats » ci-dessus. Un « oui » sans nom ne rend rien
              // assignable : la pastille ne doit pas prétendre le contraire.
              const aCandidatExploitable = reps.some((x) => x.positive && x.personne_id != null && x.personne_id !== dejaAssigne);
              const ratee = r.echeance ? classeEcheance(r.echeance) === 'echeance-ratee' : false;
              const etatDot = aCandidatExploitable ? 'done' : r.clos ? 'done-empty' : ratee ? 'late' : 'current';
              const glyphe = etatDot === 'done' || etatDot === 'done-empty' ? '✓' : etatDot === 'late' ? '!' : '';
              // Détail ouvert par défaut si round actif ou porteur d'un « oui »
              // (les rounds clos sans réponse positive = historique, repliés).
              const ouvertParDefaut = !r.clos || aPositive;
              // Recherche élargie (e-mails au vivier externe) : l'intervenant·e en voit
              // seulement l'existence — le serveur ne lui en renvoie d'ailleurs pas le détail.
              const elargie = !peutGerer && r.canal === 'email_externe';
              const ouvert = elargie ? false : (r.id in detailOverride ? detailOverride[r.id] : ouvertParDefaut);
              const form = formPourRound(r.id);
              const msgVisible = messageVisible[r.id] ?? false;
              const formOuvert = formVisible[r.id] ?? false;
              return (
                <div className="rech-step" key={r.id}>
                  <div className="rech-rail">
                    <div className={`rech-dot ${etatDot}`}>{glyphe}</div>
                    {i < recherches.length - 1 && <div className="rech-line" />}
                  </div>
                  <div className="rech-step-body">
                    <div
                      className="rech-step-head"
                      role="button"
                      tabIndex={0}
                      aria-expanded={ouvert}
                      onClick={() => { if (!elargie) setDetailOverride((p) => ({ ...p, [r.id]: !ouvert })); }}
                      onKeyDown={(e) => { if (!elargie && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setDetailOverride((p) => ({ ...p, [r.id]: !ouvert })); } }}
                    >
                      <span className="rech-chevron">{elargie ? '•' : (ouvert ? '▾' : '▸')}</span>
                      <span className="rech-step-title">{libelleCanal(r.canal)}</span>
                      <span className="rech-step-meta" style={elargie ? { display: 'none' } : undefined}>
                        <span>{reps.length === 0 ? 'aucune réponse' : `${reps.length} réponse${reps.length > 1 ? 's' : ''}`}</span>
                        {r.echeance && (
                          <span className={classeEcheance(r.echeance)}>
                            échéance {formatDateCourt(r.echeance)}{ratee ? ' · en retard' : ''}
                          </span>
                        )}
                      </span>
                    </div>

                    {ouvert && (
                      <div className="rech-detail">
                        {/* Réponses reçues (lecture) */}
                        {reps.length === 0 ? (
                          <div className="text-xs text-ink-3 mb-8">Aucune réponse pour l'instant.</div>
                        ) : (
                          <div className="mb-8">
                            {reps.map((rep) => {
                              const estAssigne = rep.personne_id != null && rep.personne_id === dejaAssigne;
                              const assignable = peutAssigner && rep.positive && rep.personne_id != null && !estAssigne;
                              return (
                                <div key={rep.id} className="rech-reponse">
                                  <span className={`badge ${rep.positive ? 'badge-ok' : 'badge-plain'}`}>{rep.positive ? 'Oui' : 'Non'}</span>
                                  <span>{rep.personne_nom ?? (rep.personne_id != null ? 'Personne' : 'Sans personne précisée')}</span>
                                  {rep.commentaire && <span className="text-ink-3">— {rep.commentaire}</span>}
                                  {/* Date d'enregistrement de la réponse dans le portail. Tant
                                      que Slack n'est pas connecté en direct, c'est le moment où la
                                      gestion l'a saisie (≈ moment de la réponse) — d'où « noté·e le ». */}
                                  <span className="text-xs text-ink-3" title="Moment où la réponse a été enregistrée dans le portail">· noté·e le {formatDateHeure(rep.created_at)}</span>
                                  {assignable && (
                                    <button type="button" className="btn btn-sm btn-ok" onClick={() => assignerDepuisReponse(rep.personne_id as number, rep.personne_nom ?? 'Personne')}>Assigner</button>
                                  )}
                                  {estAssigne && <span className="assigne-ok"><UserCheck className="icon-sm" style={{ verticalAlign: 'text-bottom' }} /> assigné·e</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Le message du tour, avec son état en clair (docs/DECISIONS.md §24). Les tours plus anciens,
                            d'avant le suivi des messages, gardent le texte seul. */}
                        {(() => {
                          const slackMsg = messages.find((x) => x.canal === 'slack' && x.recherche_id === r.id);
                          if (slackMsg) {
                            return <div className="mb-8"><MessageSlack message={slackMsg} titre={r.canal === 'slack_2' ? 'Relance' : '1er appel'} onChange={() => void recharger()} copieEnAvant={false} /></div>;
                          }
                          if (r.canal === 'email_externe') {
                            const mails = messages.filter((x) => x.canal === 'email' && x.recherche_id === r.id);
                            return mails.length > 0 ? <div className="mb-8"><MessagesEmails messages={mails} onChange={() => void recharger()} /></div> : null;
                          }
                          return null;
                        })()}
                        {!messages.some((x) => x.recherche_id === r.id) && r.message && (
                          msgVisible ? (
                            <div className="mb-8">
                              <div className="bg-inset p-12 rounded-1 text-sm" style={{ whiteSpace: 'pre-wrap' }}>{r.message}</div>
                              <div className="flex gap-8 mt-8">
                                <button type="button" className="btn btn-sm" onClick={() => copier(r.message ?? '')}><Copy className="icon-sm" /> Copier</button>
                                <button type="button" className="rech-toggle-link" onClick={() => setMessageVisible((p) => ({ ...p, [r.id]: false }))}>Masquer</button>
                              </div>
                            </div>
                          ) : (
                            <button type="button" className="rech-toggle-link" onClick={() => setMessageVisible((p) => ({ ...p, [r.id]: true }))}>Voir le message · Copier</button>
                          )
                        )}

                        {/* Ajouter une réponse (masqué par défaut) */}
                        {formOuvert ? (
                          <div className="border-t pt-12 mt-8">
                            <div className="font-medium text-ink-1 mb-8">Noter une réponse reçue</div>
                            <PersonneSelect id={`rep-personne-${r.id}`} label="Personne (optionnel)" value={form.personneId} onChange={(v) => majForm(r.id, { personneId: v })} filtres={peutGerer ? { actif: true } : { actif: true, externe: false }} />
                            <div className="selectable-group mb-12">
                              <button type="button" className={`selectable ${form.positive ? 'active' : ''}`} onClick={() => majForm(r.id, { positive: true })}>Oui</button>
                              <button type="button" className={`selectable ${!form.positive ? 'active' : ''}`} onClick={() => majForm(r.id, { positive: false })}>Non</button>
                            </div>
                            <div className="form-group">
                              <label className="form-label" htmlFor={`rep-commentaire-${r.id}`}>Commentaire</label>
                              <textarea id={`rep-commentaire-${r.id}`} className="form-textarea" value={form.commentaire} onChange={(e) => majForm(r.id, { commentaire: e.target.value })} />
                            </div>
                            <div className="flex gap-8">
                              <button type="button" className="btn btn-sm btn-accent" onClick={() => ajouterReponse(r.id)}>Enregistrer la réponse</button>
                              <button type="button" className="btn btn-sm" onClick={() => setFormVisible((p) => ({ ...p, [r.id]: false }))}>Annuler</button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-4">
                            <button type="button" className="rech-toggle-link" onClick={() => setFormVisible((p) => ({ ...p, [r.id]: true }))}>+ Noter une réponse reçue</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        );
      })())}

      {/* SMS d'annulation du jour (urgence) : numéros et texte à copier, personnes à appeler, « Marquer comme envoyés ». */}
      {onglet === 'recherche' && peutGerer && messages.some((x) => x.canal === 'sms') && (
        <MessagesSms
          demandeId={demande.id}
          messages={messages.filter((x) => x.canal === 'sms')}
          titre={`Annulation de ${demande.atelier_nom ?? 'l\'atelier'}, aujourd'hui`}
          onChange={() => void recharger()}
        />
      )}

      {onglet === 'passation' && (
        passation == null ? (
          <div className="empty-state">
            <div className="empty-state-title">Passation non initiée</div>
            <div className="empty-state-desc">Elle démarre une fois le statut Tipee confirmé.</div>
          </div>
        ) : (
          <>
            <div className="card">
              <div className="card-title mb-8">Étape 1 — Logistique</div>
              <table className="table table-compact">
                <tbody>
                  <tr><td className="text-ink-3" style={{ width: 160 }}>Échéance</td><td className={classeEcheance(passation.logistique_echeance)}>{formatDateCourt(passation.logistique_echeance)}</td></tr>
                  <tr>
                    <td className="text-ink-3">Transmise le</td>
                    <td>{passation.logistique_transmise_at ? formatDateHeure(passation.logistique_transmise_at) : peutGerer ? (
                      <button type="button" className="btn btn-sm" onClick={() => majPassation({ logistique_transmise_at: new Date().toISOString() })}>Marquer transmise</button>
                    ) : <span className="text-xs text-ink-3">En attente</span>}</td>
                  </tr>
                  <tr>
                    <td className="text-ink-3">Validée le</td>
                    <td>{passation.logistique_validee_at ? formatDateHeure(passation.logistique_validee_at) : peutGerer ? (
                      <button type="button" className="btn btn-sm" onClick={() => majPassation({ logistique_validee_at: new Date().toISOString() })}>Marquer validée</button>
                    ) : <span className="text-xs text-ink-3">En attente</span>}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="card">
              <div className="card-title mb-8">Étape 2 — Participants</div>
              <table className="table table-compact">
                <tbody>
                  <tr><td className="text-ink-3" style={{ width: 160 }}>Échéance</td><td className={classeEcheance(passation.participants_echeance)}>{formatDateCourt(passation.participants_echeance)}</td></tr>
                  <tr>
                    <td className="text-ink-3">Transmis le</td>
                    <td>{passation.participants_transmis_at ? formatDateHeure(passation.participants_transmis_at) : peutGerer ? (
                      <button type="button" className="btn btn-sm" onClick={() => majPassation({ participants_transmis_at: new Date().toISOString() })}>Marquer transmis</button>
                    ) : <span className="text-xs text-ink-3">En attente</span>}</td>
                  </tr>
                  <tr>
                    <td className="text-ink-3">Validés le</td>
                    <td>{passation.participants_valides_at ? formatDateHeure(passation.participants_valides_at) : peutGerer ? (
                      <button type="button" className="btn btn-sm" onClick={() => majPassation({ participants_valides_at: new Date().toISOString() })}>Marquer validés</button>
                    ) : <span className="text-xs text-ink-3">En attente</span>}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {passation.visite_requise && (
              <div className="card">
                <div className="card-title mb-8">Visite d'accueil</div>
                <div className="form-group">
                  <label className="form-label" htmlFor="visite-date">Date de la visite</label>
                  {peutGerer ? (
                    <input id="visite-date" type="date" className="form-input" value={passation.visite_date ?? ''} onChange={(e) => majPassation({ visite_date: e.target.value })} />
                  ) : (
                    <div className="text-sm text-ink-2">{passation.visite_date ? formatDateCourt(passation.visite_date) : 'Non planifiée'}</div>
                  )}
                </div>
                {passation.visite_faite_at ? (
                  <div className="text-sm text-ok">Visite faite le {formatDateHeure(passation.visite_faite_at)}</div>
                ) : peutGerer ? (
                  <button type="button" className="btn btn-sm" onClick={() => majPassation({ visite_faite_at: new Date().toISOString() })}>Visite faite</button>
                ) : (
                  <span className="text-xs text-ink-3">En attente</span>
                )}
              </div>
            )}

            {passation.escalade_responsable_at && alerteErreur(`Escaladé au/à la responsable le ${formatDateHeure(passation.escalade_responsable_at)}`)}

            <div className="card">
              <div className="card-title mb-8">Participants du programme</div>
              {participants.length === 0 ? (
                <div className="text-xs text-ink-3">{peutGerer ? 'Aucun participant renseigné.' : 'Aucun participant à afficher pour le moment. Ils apparaissent une fois le remplacement validé par la gestion.'}</div>
              ) : (
                <table className="table table-compact">
                  <thead><tr><th>Nom</th>{peutGerer && <th>Téléphone</th>}<th>Remarques</th></tr></thead>
                  <tbody>
                    {participants.map((p) => (
                      <tr key={p.id}><td>{p.nom}</td>{peutGerer && <td>{p.telephone ?? '—'}</td>}<td>{p.remarques ?? '—'}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )
      )}

      {onglet === 'presences' && (
        presences == null ? (
          <div className="empty-state">
            <div className="empty-state-title">Liste de présence non générée</div>
          </div>
        ) : (
          <div className="card">
            <table className="table table-compact">
              <tbody>
                <tr>
                  <td className="text-ink-3" style={{ width: 160 }}>Envoyée</td>
                  <td>{presences.envoyee_at ? formatDateHeure(presences.envoyee_at) : peutGerer ? <button type="button" className="btn btn-sm" onClick={() => majPresences({ envoyee_at: new Date().toISOString() })}>Marquer envoyée</button> : <span className="text-xs text-ink-3">En attente</span>}</td>
                </tr>
                <tr>
                  <td className="text-ink-3">Remplie</td>
                  <td>{presences.remplie_at ? formatDateHeure(presences.remplie_at) : peutGerer ? <button type="button" className="btn btn-sm" onClick={() => majPresences({ remplie_at: new Date().toISOString() })}>Marquer remplie</button> : <span className="text-xs text-ink-3">En attente</span>}</td>
                </tr>
                <tr>
                  <td className="text-ink-3">Retournée</td>
                  <td>{presences.retournee_at ? formatDateHeure(presences.retournee_at) : peutGerer ? <button type="button" className="btn btn-sm" onClick={() => majPresences({ retournee_at: new Date().toISOString() })}>Marquer retournée</button> : <span className="text-xs text-ink-3">En attente</span>}</td>
                </tr>
                <tr>
                  <td className="text-ink-3">Contrôlée</td>
                  <td>{presences.controlee_at ? formatDateHeure(presences.controlee_at) : peutGerer ? <button type="button" className="btn btn-sm" onClick={() => majPresences({ controlee_at: new Date().toISOString() })}>Marquer contrôlée</button> : <span className="text-xs text-ink-3">En attente</span>}</td>
                </tr>
                <tr>
                  <td className="text-ink-3">Complète</td>
                  <td>
                    {peutGerer ? (
                      <div className="selectable-group">
                        <button type="button" className={`selectable ${presences.complete === true ? 'active' : ''}`} onClick={() => majPresences({ complete: true })}>Oui</button>
                        <button type="button" className={`selectable ${presences.complete === false ? 'active' : ''}`} onClick={() => majPresences({ complete: false })}>Non</button>
                      </div>
                    ) : (
                      <span className="text-sm text-ink-2">{presences.complete === true ? 'Oui' : presences.complete === false ? 'Non' : '—'}</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="text-ink-3">Relance</td>
                  <td>{presences.relance_at ? formatDateHeure(presences.relance_at) : peutGerer ? <button type="button" className="btn btn-sm" onClick={() => majPresences({ relance_at: new Date().toISOString() })}>Relancer</button> : <span className="text-xs text-ink-3">En attente</span>}</td>
                </tr>
              </tbody>
            </table>
            {presences.notes && <div className="text-sm text-ink-2 mt-12">{presences.notes}</div>}
          </div>
        )
      )}

      {onglet === 'fak' && (
        <>
          <div className="card">
            <div className="card-header">
              <div className="card-title">Fiche relais d'atelier (FRA)</div>
              {fak && <span className={classeBadgeFak(fak.statut)}>{LIBELLES_STATUT_FAK[fak.statut]}</span>}
            </div>
            {!fak ? (
              <div className="text-xs text-ink-3">Aucune FRA pour cet atelier.</div>
            ) : (
              <>
                {fak.lien_document && <a href={fak.lien_document} target="_blank" rel="noreferrer" className="btn btn-sm mb-12">Ouvrir le document</a>}
                {fak.contenu && <div className="bg-inset p-12 rounded-1 text-sm" style={{ whiteSpace: 'pre-wrap' }}>{fak.contenu}</div>}
              </>
            )}
          </div>

          {fak && (
            <div className="card">
              <div className="card-title mb-8">Retours d'expérience</div>
              {fakFeedback.length === 0 ? (
                <div className="text-xs text-ink-3 mb-12">Aucun retour pour l'instant.</div>
              ) : (
                <div className="flex flex-col gap-8 mb-12">
                  {fakFeedback.map((f) => (
                    <div key={f.id} className="border-b pb-8">
                      <div className="text-sm">{f.contenu}</div>
                      <div className="text-xs text-ink-3">{f.auteur_nom} · {formatRelatif(f.created_at)}{f.integre ? ' · Intégré' : ''}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="form-group">
                <label className="form-label" htmlFor="fak-feedback">Ajouter un retour</label>
                <textarea id="fak-feedback" className="form-textarea" value={feedbackTexte} onChange={(e) => setFeedbackTexte(e.target.value)} />
              </div>
              <button type="button" className="btn btn-sm btn-accent" onClick={ajouterFeedback}>Envoyer</button>
            </div>
          )}
        </>
      )}

      {onglet === 'historique' && (
        events.length === 0 ? (
          <div className="empty-state"><div className="empty-state-title">Aucun événement</div></div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Événement</th>
                <th>Auteur</th>
              </tr>
            </thead>
            <tbody>
              {[...events].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map((ev) => (
                <tr key={ev.id}>
                  <td className="text-ink-3 tabular-nums" style={{ whiteSpace: 'nowrap' }} title={formatDateHeure(ev.created_at)}>
                    {formatRelatif(ev.created_at)}
                  </td>
                  <td className="font-medium text-ink-1">
                    {libelleEvenement(ev.event_type, peutGerer)}
                    {(ev.event_type === 'remplacant_refuse' || ev.event_type === 'remplacant_confirme')
                      && typeof (ev.details as Record<string, unknown> | undefined)?.remplacant_nom === 'string'
                      && <span className="text-ink-3 font-normal"> · {String((ev.details as Record<string, unknown>).remplacant_nom)}</span>}
                    {ev.event_type === 'prevenus_personnellement' && typeof (ev.details as Record<string, unknown> | undefined)?.nb === 'number'
                      && <span className="text-ink-3 font-normal"> · {Number((ev.details as Record<string, unknown>).nb)} message{Number((ev.details as Record<string, unknown>).nb) > 1 ? 's' : ''} envoyé{Number((ev.details as Record<string, unknown>).nb) > 1 ? 's' : ''}</span>}
                  </td>
                  <td className="text-ink-3">{ev.actor_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {modal && (
        // Pas de fermeture au clic sur l'overlay (motif, sélection de
        // remplaçant·e… : un clic à côté ne doit jamais effacer une saisie).
        // Fermeture explicite uniquement : ✕, « Annuler », ou Échap.
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{titreModal(modal)}</h3>
              <button type="button" className="close-btn" onClick={() => setModal(null)} aria-label="Fermer">✕</button>
            </div>
            <div className="modal-body">
              {erreurAction && <div className="text-err text-sm mb-12">{erreurAction}</div>}
              {modal.type === 'remplacant' && (
                <>
                  {mRemplacantPrefillNom && (
                    <div className="badge badge-ok mb-12" style={{ display: 'block', padding: '8px 12px', textTransform: 'none', letterSpacing: 'normal', fontSize: 'var(--t-13)' }}>
                      Pré-rempli depuis la réponse « oui » de {mRemplacantPrefillNom}. Vérifiez puis confirmez.
                    </div>
                  )}
                  <PersonneSelect id="modal-remplacant" label="Remplaçant·e" value={mRemplacantId} onChange={setMRemplacantId} filtres={peutGerer ? { actif: true } : { actif: true, externe: false }} required />
                  <label className="flex items-center gap-8 text-sm mb-12">
                    <input type="checkbox" checked={mStagiaire} onChange={(e) => setMStagiaire(e.target.checked)} /> Stagiaire
                  </label>
                  {mStagiaire && <PersonneSelect id="modal-garant" label="Garant·e du cadre" value={mGarantId} onChange={setMGarantId} filtres={peutGerer ? { actif: true } : { actif: true, externe: false }} required />}
                </>
              )}
              {modal.type === 'tipee_completer' && (
                <div className="form-group">
                  <p className="text-sm text-ink-2 mb-8">
                    {demande.absent_nom ?? 'La personne absente'} sera prévenu·e et la demande restera en attente de
                    la validation dans Tipee.
                  </p>
                  <label className="form-label" htmlFor="modal-tipee">Ce qui manque dans la demande Tipee</label>
                  <textarea id="modal-tipee" className="form-textarea" value={mMotif} onChange={(e) => setMMotif(e.target.value)} required />
                </div>
              )}
              {modal.type === 'motif' && (
                <div className="form-group">
                  <label className="form-label" htmlFor="modal-motif">{modal.label}</label>
                  <textarea id="modal-motif" className="form-textarea" value={mMotif} onChange={(e) => setMMotif(e.target.value)} required />
                </div>
              )}
              {modal.type === 'fusion' && (
                <>
                  <div className="form-group">
                    <label className="form-label" htmlFor="modal-atelier-cible">Atelier cible</label>
                    <select id="modal-atelier-cible" className="form-select" value={mAtelierCible ?? ''} onChange={(e) => setMAtelierCible(e.target.value ? Number(e.target.value) : null)}>
                      <option value="">Sélectionner…</option>
                      {ateliersCible.map((a) => <option key={a.id} value={a.id}>{a.nom}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="modal-lieu-horaire">Lieu / horaire</label>
                    <input id="modal-lieu-horaire" type="text" className="form-input" value={mLieuHoraire} onChange={(e) => setMLieuHoraire(e.target.value)} />
                  </div>
                </>
              )}
              {modal.type === 'cloture' && (
                <div className="form-group">
                  <label className="form-label" htmlFor="modal-facturation">Facturation</label>
                  <select id="modal-facturation" className="form-select" value={mFacturation} onChange={(e) => setMFacturation(e.target.value as 'heures_supp' | 'facture_externe' | 'aucune')}>
                    <option value="aucune">Aucune</option>
                    <option value="heures_supp">Heures supplémentaires</option>
                    <option value="facture_externe">Facture externe</option>
                  </select>
                </div>
              )}
              {modal.type === 'confirmer_refus' && (
                <p className="text-sm text-ink-2">
                  La demande repartira en recherche d'un·e remplaçant·e. Confirmez-vous ?
                </p>
              )}
              {modal.type === 'confirmer' && envoiDemandeTipee && demande && (
                <>
                  <div style={{ background: 'var(--accent-dim)', borderRadius: 'var(--r1)', padding: '10px 12px', marginBottom: 12 }}>
                    <span className="font-medium">Repérée dans Tipee.</span>{' '}
                    {envoiMaDemande
                      ? `Nous avons remarqué votre absence du ${formatDateCourt(demande.date_atelier)} dans Tipee et préparé cette demande pour vous.`
                      : `${demande.absent_nom ?? 'La personne absente'} n'a pas saisi cette demande : l'app l'a créée à partir de son absence.`}
                  </div>
                  <p className="text-sm text-ink-2">
                    {envoiMaDemande
                      ? "Une fois envoyée, il vous reste à trouver votre remplaçant·e. L'équipe gestion vous aidera si besoin."
                      : "Si un·e remplaçant·e n'est finalement pas nécessaire, choisissez plutôt « Écarter »."}
                  </p>
                </>
              )}
              {modal.type === 'confirmer' && !envoiDemandeTipee && (
                <p className="text-sm text-ink-2">
                  {DESCRIPTIONS_ACTION[modal.action] ?? 'Confirmez-vous cette action ?'}
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn" onClick={() => setModal(null)}>{envoiMaDemande ? 'Pas maintenant' : 'Retour'}</button>
              <button
                type="button"
                className={`btn ${actionDangereuse(modal) ? 'btn-danger' : 'btn-accent'}`}
                onClick={soumettreModal}
                disabled={actionEnCours != null}
              >
                {actionEnCours ? <span className="spinner spinner-sm" /> : libelleValider(modal)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
